import DOMPurify from 'dompurify';

/**
 * Type definition for allowed HTML tags and their attributes.
 *
 * @typedef {Object} AllowedHtmlTag
 * @property {string[]} ALLOWED_ATTR - List of allowed attributes for the HTML tag.
 * @property {Record<string, string>} [OVERWRITE_ATTR] - Optional attributes to overwrite for the HTML tag.
 */
export type AllowedHtmlTag = {
  ALLOWED_ATTR: string[];
  OVERWRITE_ATTR?: Record<string, string>;
};

/**
 * Allowed HTML tags and attributes for chatbot messages.
 *
 * @constant {Record<string, AllowedHtmlTag>}
 */
export const ALLOWED_HTML_TAGS: Record<string, AllowedHtmlTag> = {
  a: {
    ALLOWED_ATTR: ['href', 'target', 'rel', 'title'],
    OVERWRITE_ATTR: {
      target: '_blank',
      rel: 'noopener noreferrer',
    },
  },
};

/**
 * Sanitizes the provided HTML string by allowing only specific tags and attributes.
 *
 * @param {string} html - The HTML string to sanitize.
 * @returns {string} - The sanitized HTML string.
 */
export const sanitizeHTML = (
  html: string,
  allowedTags = ALLOWED_HTML_TAGS,
): string => {
  const config = {
    ALLOWED_TAGS: Object.keys(allowedTags),
    ALLOWED_ATTR: Object.values(allowedTags)
      .map((tag) => tag.ALLOWED_ATTR)
      .flat(),
  };

  // Add a hook to overwrite attributes after sanitization
  DOMPurify.addHook('afterSanitizeAttributes', function (node) {
    Object.entries(allowedTags).forEach(([tag, { OVERWRITE_ATTR }]) => {
      if (OVERWRITE_ATTR && node.tagName === tag.toUpperCase()) {
        Object.entries(OVERWRITE_ATTR).forEach(([attr, value]) => {
          node.setAttribute(attr, value);
        });
      }
    });
  });
  return DOMPurify.sanitize(html, config);
};
